export default {
  async fetch_backend_message_page(conversation_id, params = {}) {
    return this.fetch_assist_history({
      chat_id: conversation_id,
      limit: 50,
      ...params,
    });
  },

  merge_backend_conversation_page(backend_conversations, options = {}) {
    const incoming_ids = new Set(backend_conversations.map((conversation) => conversation.id));
    const existing_by_id = new Map(
      this.conversations.map((conversation) => [conversation.id, conversation])
    );
    const merged_incoming = backend_conversations.map((conversation) => (
      this.merge_existing_conversation_state(existing_by_id.get(conversation.id), conversation)
    ));

    if (options.append) {
      const existing_conversations = this.conversations.filter(
        (conversation) => !incoming_ids.has(conversation.id)
      );
      return [...existing_conversations, ...merged_incoming];
    }

    const local_drafts = this.conversations.filter((conversation) => (
      conversation.source !== "backend" && !incoming_ids.has(conversation.id)
    ));
    return [...local_drafts, ...merged_incoming];
  },

  merge_existing_conversation_state(existing_conversation, incoming_conversation) {
    if (!existing_conversation) return incoming_conversation;
    const merged_conversation = this.merge_conversation_summary(
      existing_conversation,
      incoming_conversation
    );
    return ({
      ...merged_conversation,
      messages: existing_conversation.messages?.length
        ? existing_conversation.messages
        : merged_conversation.messages,
      messages_loaded: existing_conversation.messages_loaded
        || merged_conversation.messages_loaded,
      messages_next_cursor: existing_conversation.messages_next_cursor
        || merged_conversation.messages_next_cursor,
      is_loading_messages: false,
    });
  },

  merge_conversation_summary(existing_conversation, incoming_conversation) {
    const existing_updated_at = this.get_timestamp_sort_value(existing_conversation.updated_at);
    const incoming_updated_at = this.get_timestamp_sort_value(incoming_conversation.updated_at);
    const latest_summary = existing_updated_at > incoming_updated_at
      ? existing_conversation
      : incoming_conversation;
    const existing_created_at = this.get_timestamp_sort_value(existing_conversation.created_at);
    const incoming_created_at = this.get_timestamp_sort_value(incoming_conversation.created_at);
    const earliest_summary = existing_created_at <= incoming_created_at
      ? existing_conversation
      : incoming_conversation;

    return ({
      ...incoming_conversation,
      title: earliest_summary.title || incoming_conversation.title,
      preview: latest_summary.preview || incoming_conversation.preview,
      created_at: earliest_summary.created_at || incoming_conversation.created_at,
      updated_at: latest_summary.updated_at || incoming_conversation.updated_at,
      message_count: Math.max(
        existing_conversation.message_count || 0,
        incoming_conversation.message_count || 0
      ),
    });
  },

  async ensure_active_conversation_messages(conversation_id = this.active_conversation_id) {
    const conversation = this.get_conversation_by_id(conversation_id);
    if (conversation?.source !== "backend") return false;
    if (conversation.messages_loaded || conversation.is_loading_messages) return false;
    try {
      return await this.load_conversation_messages(conversation_id, { replace: true });
    } catch {
      return false;
    }
  },

  async load_conversation_messages(conversation_id, options = {}) {
    const conversation = this.get_conversation_by_id(conversation_id);
    if (!conversation || conversation.is_loading_messages) return false;

    conversation.is_loading_messages = true;
    try {
      const page = await this.fetch_backend_message_page(conversation_id, {
        cursor: options.cursor || null,
      });
      const page_messages = this.history_records_to_messages(page.results);
      conversation.messages = options.prepend
        ? this.merge_message_pages(page_messages, conversation.messages)
        : page_messages;
      conversation.messages_loaded = true;
      conversation.messages_next_cursor = page.next_cursor || null;
      conversation.message_count = Math.max(
        conversation.message_count || 0,
        Math.ceil(conversation.messages.length / 2)
      );
      conversation.preview = this.get_messages_preview(conversation.messages)
        || conversation.preview;
      if (!options.prepend) this.scroll_to_latest_message({ settle: true });
      return true;
    } finally {
      conversation.is_loading_messages = false;
    }
  },

  async load_older_active_messages() {
    const conversation = this.active_conversation;
    if (!conversation?.messages_next_cursor || conversation.is_loading_messages) return false;
    const scroll_state = this.$refs.chat_messages?.capture_scroll_state?.();
    try {
      const loaded = await this.load_conversation_messages(conversation.id, {
        cursor: conversation.messages_next_cursor,
        prepend: true,
      });
      if (loaded && scroll_state) {
        this.$refs.chat_messages?.restore_scroll_state?.(scroll_state);
      }
      return loaded;
    } catch {
      return false;
    }
  },

  merge_message_pages(incoming_messages, existing_messages) {
    const existing_ids = new Set(existing_messages.map((message) => message.id));
    return [
      ...incoming_messages.filter((message) => !existing_ids.has(message.id)),
      ...existing_messages,
    ];
  },

  history_records_to_messages(records) {
    if (!Array.isArray(records)) return [];
    return [...records]
      .sort((left, right) => (
        this.get_timestamp_sort_value(left.created_at)
        - this.get_timestamp_sort_value(right.created_at)
      ))
      .flatMap((record) => this.history_record_messages(record));
  },

  history_record_messages(record) {
    const record_id = this.get_history_record_id(record);
    const messages = [
      this.create_message("user", record.query_text || "", {
        id: `${record_id}-user`,
        created_at: record.created_at,
      }),
    ];
    const assistant_text = this.get_history_record_assistant_text(record);
    if (assistant_text) {
      messages.push(
        this.create_message("assistant", assistant_text, {
          id: `${record_id}-assistant`,
          created_at: record.completed_at || record.created_at,
        })
      );
    }
    return messages;
  },

  get_history_record_assistant_text(record) {
    if (record.response_text) return record.response_text;
    const status = String(record.status || "").toUpperCase();
    if (status === "FAILED") {
      return record.error_message || "Jeffrey could not process that request.";
    }
    if (["QUEUED", "PROCESSING"].includes(status)) {
      return "Jeffrey is still working on this request.";
    }
    return "";
  },

  get_history_record_id(record) {
    const stable_id = record.id || `${record.chat_id || "default"}-${record.created_at}`;
    return `history-${stable_id}`;
  },

  get_timestamp_sort_value(value) {
    const timestamp = Date.parse(value || "");
    return Number.isNaN(timestamp) ? 0 : timestamp;
  },
};
